<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useEmitter } from 'dashboard/composables/emitter';
import { useMapGetter } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useLocale } from 'shared/composables/useLocale';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import MasifiWhatsappQrAPI from 'dashboard/api/masifiWhatsappQr';
import {
  WHATSAPP_QR_EVENT,
  WHATSAPP_QR_FEATURE,
} from 'dashboard/helper/masifiWhatsappQr';

// Panel de un número de WhatsApp por QR: checklist + aceptación de riesgo, QR en vivo, estado, pausar/reanudar,
// desvincular. Admin-only (la API lo es). Spec: whatsapp-qr-design.md §3.2/§5.1/§6.1-7.
const props = defineProps({ inbox: { type: Object, required: true } });
const emit = defineEmits(['connected']);
const { t } = useI18n();

// Capa 1 del gate: con el módulo apagado se ocultan las ACCIONES (nada llega al conector), pero `show` es de
// solo lectura (#396) y el backend ahora la deja pasar apagado (E2E fix #8, Ruling en EnsureWhatsappQrEnabled)
// porque el estado es metadata del canal — el panel lo lee igual para explicarse en vez de un cartel vacío.
const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabled = useMapGetter('accounts/isFeatureEnabledonAccount');
const moduleEnabled = computed(() =>
  isFeatureEnabled.value(accountId.value, WHATSAPP_QR_FEATURE)
);
const { isAdmin } = useAdmin();

const state = ref({ connection: 'unlinked', risk_accepted: false });
const qr = ref(null);
const riskChecked = ref(false);
const busy = ref(false);
const unlinkDialogRef = ref(null);

// Después de `state` (no-use-before-define aplica también en closures — lesson-repo-lint-hook-pitfalls).
// Fechas en el idioma de la UI con el patrón nativo (useLocale + Intl, como AccountHealth.vue) — revisión E2E fix 1, M5:
// date-fns sin locale escribía "Sep 24, 2026" en la UI en español.
const { resolvedLocale } = useLocale();
const localDateTime = value =>
  value
    ? new Intl.DateTimeFormat(resolvedLocale.value, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(value))
    : '';
const banUntil = computed(() => localDateTime(state.value.ban_until));
// Anti-baneo R5 (auditoría 2026-09-24): WhatsApp limitó las consultas del número (grupos/fotos) hasta esa hora.
const queriesPausedUntil = computed(() =>
  localDateTime(state.value.queries_paused_until)
);

const GREEN = ['open'];
const AMBER = ['pairing', 'connecting', 'closed', 'paused', 'unlinking'];
const tone = computed(() => {
  if (GREEN.includes(state.value.connection)) return 'text-n-teal-11';
  if (AMBER.includes(state.value.connection)) return 'text-n-amber-11';
  return 'text-n-ruby-11';
});
const canConnect = computed(() =>
  ['unlinked', 'closed', 'paused', 'logged_out', 'failed', 'replaced'].includes(
    state.value.connection
  )
);
const canPause = computed(() =>
  ['open', 'connecting', 'closed'].includes(state.value.connection)
);
// Second-pass review C2: connector v0.3.0 carries a session.logout only over a live socket (it never dials for
// it, and a session it is not running never reads it). So Unlink is offered only while a paired number is
// connected; a pairing that never completed is cancelled instead, and every other paired state gets a hint
// (resume first / remove it from the phone) rather than a button that can only fail again.
const paired = computed(() => Boolean(state.value.paired_at));
const canUnlink = computed(
  () => paired.value && ['open', 'connecting'].includes(state.value.connection)
);
const canCancelPairing = computed(() => state.value.connection === 'pairing');
const unlinkHint = computed(() => {
  const { connection } = state.value;
  if (
    !paired.value ||
    ['open', 'connecting', 'unlinking', 'unlinked', 'logged_out'].includes(
      connection
    )
  )
    return '';
  return connection === 'paused'
    ? t('MASIFI_WHATSAPP_QR.UNLINK_HINT_PAUSED')
    : t('MASIFI_WHATSAPP_QR.UNLINK_HINT_PHONE');
});
const errorText = computed(() => {
  const code = state.value.last_error?.code;
  return code
    ? t(
        `MASIFI_WHATSAPP_QR.ERRORS.${code.toUpperCase()}`,
        t('MASIFI_WHATSAPP_QR.ERRORS.GENERIC')
      )
    : '';
});

const apply = data => {
  state.value = { ...state.value, ...data };
  qr.value =
    data.connection === 'pairing' ? data.qr_data_url || qr.value : null;
  if (data.connection === 'open') emit('connected');
};

const run = async action => {
  busy.value = true;
  try {
    const { data } = await action();
    apply(data);
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('MASIFI_WHATSAPP_QR.ERRORS.GENERIC')
    );
  } finally {
    busy.value = false;
  }
};

const connect = () =>
  run(() =>
    MasifiWhatsappQrAPI.connect(props.inbox.id, {
      riskAccepted: riskChecked.value,
    })
  );
const pause = () => run(() => MasifiWhatsappQrAPI.pause(props.inbox.id));
// Rule 6 (native components, no window.confirm): the native Dialog asks for confirmation; the API call
// only happens on its `confirm` event (F5, final review).
const unlink = () => unlinkDialogRef.value.open();
// E2E fix #1(a) (Marcos, live test 2026-09-23): confirmUnlink never closed the Dialog, so a user who saw no
// feedback clicked "Desvincular" again on what looked like a stuck dialog, firing a second session.logout
// while the first was still in flight. Close it up front (the panel's own `busy` state, already gating every
// action button, keeps a second click from reopening it before the request resolves).
const confirmUnlink = () => {
  unlinkDialogRef.value.close();
  run(() => MasifiWhatsappQrAPI.unlink(props.inbox.id));
};
// Nothing is linked yet, so there is nothing to confirm: the backend cancels the pairing run (C1).
const cancelPairing = () =>
  run(() => MasifiWhatsappQrAPI.unlink(props.inbox.id));

// F2 ruling 13: ajustes por número (default on). Se muestran cuando el payload de la sesión ya los trae (la
// sesión existe) y con el módulo encendido (el PATCH pasa por el gate). Toggle optimista: revierte si falla.
const hasSettings = computed(
  () => typeof state.value.mark_as_read === 'boolean'
);
const updateSetting = async (key, apiKey, value) => {
  const previous = state.value[key];
  state.value = { ...state.value, [key]: value };
  try {
    const { data } = await MasifiWhatsappQrAPI.updateSettings(props.inbox.id, {
      [apiKey]: value,
    });
    // Solo las claves de ajustes: `apply` re-emitiría `connected` (el paso final del alta avanza con eso).
    state.value = {
      ...state.value,
      mark_as_read: data.mark_as_read,
      show_reactions: data.show_reactions,
      groups: data.groups,
    };
  } catch (error) {
    state.value = { ...state.value, [key]: previous };
    useAlert(
      error?.response?.data?.error || t('MASIFI_WHATSAPP_QR.ERRORS.GENERIC')
    );
  }
};
const updateMarkAsRead = value =>
  updateSetting('mark_as_read', 'markAsRead', value);
const updateShowReactions = value =>
  updateSetting('show_reactions', 'showReactions', value);
const updateReceiveGroups = value => updateSetting('groups', 'groups', value);

useEmitter(WHATSAPP_QR_EVENT, data => {
  if (data.inbox_id === props.inbox.id) apply(data);
});

onMounted(() => {
  run(() => MasifiWhatsappQrAPI.get(props.inbox.id));
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <p class="text-sm font-medium" :class="tone">
      {{ t(`MASIFI_WHATSAPP_QR.STATE.${state.connection.toUpperCase()}`) }}
    </p>
    <!-- E2E fix #8 (Marcos, live test 2026-09-23): with the module off, the real (read-only) state above still
    shows (e.g. "Pausado" once the gate paused it) -- this just explains why, and points an admin at the knob. -->
    <p
      v-if="!moduleEnabled"
      data-test="wa-qr-module-off"
      class="text-sm text-n-slate-11"
    >
      {{ t('MASIFI_WHATSAPP_QR.MODULE_OFF_HINT') }}
      <router-link
        v-if="isAdmin"
        :to="{ name: 'masifi_settings_index', params: { accountId } }"
        class="underline"
      >
        {{ t('MASIFI_WHATSAPP_QR.MODULE_OFF_LINK') }}
      </router-link>
    </p>
    <p
      v-if="banUntil"
      data-test="wa-qr-ban-until"
      class="text-sm text-n-slate-11"
    >
      {{ t('MASIFI_WHATSAPP_QR.BAN_UNTIL', { date: banUntil }) }}
    </p>
    <p
      v-if="queriesPausedUntil"
      data-test="wa-qr-queries-paused"
      class="text-sm text-n-amber-11"
    >
      {{ t('MASIFI_WHATSAPP_QR.QUERIES_PAUSED', { date: queriesPausedUntil }) }}
    </p>
    <p v-if="errorText" class="text-sm text-n-slate-11">{{ errorText }}</p>
    <p
      v-if="moduleEnabled && unlinkHint"
      data-test="wa-qr-unlink-hint"
      class="text-sm text-n-slate-11"
    >
      {{ unlinkHint }}
    </p>
    <!-- E2E fix #9 (connector investigation 2026-09-23): connector v0.3.0 never acks the phone's history
    sync, so the device can keep showing "Sincronizando" in the phone's own Linked devices list even after a
    real unlink (upstream connector limitation). Point the admin at the phone-side fix. -->
    <p
      v-if="state.connection === 'logged_out'"
      data-test="wa-qr-sync-hint"
      class="text-sm text-n-slate-11"
    >
      {{ t('MASIFI_WHATSAPP_QR.SYNC_HINT') }}
    </p>

    <!-- E2E fix #8: no actions while the module is off -- checklist, QR and the connect/pause/unlink buttons
    all stay gated (layer 1), matching the backend (only `show` is allowed through layer 3 when off). -->
    <template v-if="moduleEnabled">
      <div
        v-if="!state.risk_accepted"
        data-test="wa-qr-risk"
        class="flex flex-col gap-2 text-sm text-n-slate-12"
      >
        <p class="font-medium">{{ t('MASIFI_WHATSAPP_QR.RISK.TITLE') }}</p>
        <ul class="list-disc ps-5 text-n-slate-11">
          <li
            v-for="item in [
              'TOS',
              'BUSINESS',
              'AGE',
              'PROFILE',
              'PHONE_ONLINE',
              'NO_OTHER_TOOL',
            ]"
            :key="item"
          >
            {{ t(`MASIFI_WHATSAPP_QR.RISK.${item}`) }}
          </li>
        </ul>
        <label class="flex items-center gap-2">
          <Checkbox v-model="riskChecked" />
          {{ t('MASIFI_WHATSAPP_QR.RISK.ACCEPT') }}
        </label>
      </div>

      <img
        v-if="qr"
        data-test="wa-qr-image"
        :src="qr"
        :alt="t('MASIFI_WHATSAPP_QR.QR_ALT')"
        class="size-64"
      />
      <p v-if="state.connection === 'pairing'" class="text-sm text-n-slate-11">
        {{ t('MASIFI_WHATSAPP_QR.SCAN_HELP') }}
      </p>

      <div class="flex gap-2">
        <NextButton
          v-if="canConnect"
          :label="
            t(
              state.connection === 'paused'
                ? 'MASIFI_WHATSAPP_QR.RESUME'
                : 'MASIFI_WHATSAPP_QR.CONNECT'
            )
          "
          :is-loading="busy"
          :disabled="busy || (!state.risk_accepted && !riskChecked)"
          @click="connect"
        />
        <NextButton
          v-if="canPause"
          slate
          :label="t('MASIFI_WHATSAPP_QR.PAUSE')"
          :disabled="busy"
          @click="pause"
        />
        <NextButton
          v-if="canUnlink"
          ruby
          faded
          data-test="wa-qr-unlink"
          :label="t('MASIFI_WHATSAPP_QR.UNLINK')"
          :disabled="busy"
          @click="unlink"
        />
        <NextButton
          v-if="canCancelPairing"
          slate
          faded
          data-test="wa-qr-cancel-pairing"
          :label="t('MASIFI_WHATSAPP_QR.CANCEL_PAIRING')"
          :disabled="busy"
          @click="cancelPairing"
        />
      </div>

      <template v-if="hasSettings">
        <SettingsToggleSection
          :model-value="state.mark_as_read"
          :header="t('MASIFI_WHATSAPP_QR.SETTINGS.MARK_AS_READ')"
          :description="t('MASIFI_WHATSAPP_QR.SETTINGS.MARK_AS_READ_HELP')"
          @update:model-value="updateMarkAsRead"
        />
        <SettingsToggleSection
          :model-value="state.show_reactions"
          :header="t('MASIFI_WHATSAPP_QR.SETTINGS.SHOW_REACTIONS')"
          :description="t('MASIFI_WHATSAPP_QR.SETTINGS.SHOW_REACTIONS_HELP')"
          @update:model-value="updateShowReactions"
        />
        <SettingsToggleSection
          :model-value="state.groups"
          :header="t('MASIFI_WHATSAPP_QR.SETTINGS.RECEIVE_GROUPS')"
          :description="t('MASIFI_WHATSAPP_QR.SETTINGS.RECEIVE_GROUPS_HELP')"
          @update:model-value="updateReceiveGroups"
        />
      </template>
    </template>

    <Dialog
      ref="unlinkDialogRef"
      type="alert"
      :title="t('MASIFI_WHATSAPP_QR.UNLINK_DIALOG_TITLE')"
      :description="`${t('MASIFI_WHATSAPP_QR.UNLINK_CONFIRM')} ${t('MASIFI_WHATSAPP_QR.SYNC_HINT')}`"
      :confirm-button-label="t('MASIFI_WHATSAPP_QR.UNLINK')"
      :cancel-button-label="t('MASIFI_WHATSAPP_QR.CANCEL')"
      @confirm="confirmUnlink"
    />
  </div>
</template>
